/**
 * The address a page was last at, for the links that go back to it.
 *
 * The URL is the state (`useSearchState`), which answers "what am I looking at"
 * perfectly and answers nothing at all about a page you have **left**: a task page's
 * own link back, and the sidebar's Tasks item, both address `/studio/tasks` with no
 * search at all, so returning from a task threw away the filters that found it. The
 * browser's Back button already restores them; a link cannot, because it is a new
 * navigation and there is nothing in it to restore from.
 *
 * `sessionStorage`, like `rememberScrollY` beside it: it is one tab's memory of one
 * visit, it must not outlive the window, and a browser that refuses to store it
 * leaves a link that goes to the unfiltered board — which is where the link went
 * before this existed.
 */

/** The Tasks page's slot. One key per page that has a link pointing back at it. */
export const TASKS_SEARCH_KEY = 'fes.studio.tasks.search'

/** Remember where a page is, verbatim. */
export function rememberSearch(key: string, search: Record<string, unknown>): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(search))
  } catch {
    /* private mode / quota — the link still works, it just starts clean */
  }
}

/**
 * Where that page was, or `{}`.
 *
 * `{}` rather than undefined because every caller is a `Link`'s `search`, and the
 * page's own schema fills in every default from an empty object — so "nothing
 * remembered" and "the unfiltered board" are the same address, which they are.
 */
export function recallSearch(key: string): Record<string, unknown> {
  try {
    const raw = sessionStorage.getItem(key)
    if (raw === null) return {}
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {}
    return parsed as Record<string, unknown>
  } catch {
    return {}
  }
}
